import { useContext, useEffect, useMemo, useState } from "react";
import { Empty, Loading, Switch } from "components/ui";
import Heading from "components/ui/Heading/Heading";
import AddButton from "components/ui/Button/AddButton/AddButton";
import PaginationSearch from "components/ui/InputTypes/InputSearch/PaginationSearch/PaginationSearch";
import ErrorView from "components/ui/ErrorView/ErrorView";
import { ReactSVG } from "react-svg";
import asset from "assets/images";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";
import ComponentAccessControl from "components/essentials/ComponentAccessControl/ComponentAccessControl";
import { apiRoute } from "services";
import API from "services/lib/api";
import { dataQueryStatus } from "utils";
import { getErrorMessage } from "utils/helper";
import {
    CUSTOM_TICKET_STATUS_CREATE_PERMISSION,
    CUSTOM_TICKET_STATUS_DELETE_PERMISSION,
    CUSTOM_TICKET_STATUS_UPDATE_PERMISSION,
} from "./permissions";
import CreateCustomTicketStatusModal from "./CreateCustomTicketStatusModal/CreateCustomTicketStatusModal";
import "./CustomTicketStatuses.scss";

const { IDLE, LOADING, ERROR, DATAMODE, NULLMODE } = dataQueryStatus;

const formatCreatedDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const CustomTicketStatuses = () => {
    const toastMessage = useContext(ToastContext);
    const [status, setStatus] = useState(IDLE);
    const [errorMssg, setErrorMssg] = useState();
    const [payload, setPayload] = useState({
        summary: { total: 0, active: 0, inAutomation: 0 },
        categories: [],
    });
    const [search, setSearch] = useState("");
    const [searchIsLoading, setSearchIsLoading] = useState(false);
    const [clearSearch, setClearSearch] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);

    const getStatuses = async (searchValue = search) => {
        try {
            setErrorMssg();
            if (!payload?.categories?.length) {
                setStatus(LOADING);
            }
            setSearchIsLoading(true);

            const res = await API.get(apiRoute.getCustomTicketStatuses, {
                params: searchValue?.trim()
                    ? { search: searchValue.trim() }
                    : undefined,
            });

            if (res.status === 200) {
                const data = res?.data?.data || {
                    summary: { total: 0, active: 0, inAutomation: 0 },
                    categories: [],
                };
                setPayload(data);

                const nextExpanded = {};
                (data.categories || []).forEach((category) => {
                    nextExpanded[category.globalCategory] =
                        expanded[category.globalCategory] ??
                        category.count > 0;
                });
                setExpanded(nextExpanded);

                const hasStatuses = (data.summary?.total || 0) > 0;
                setStatus(hasStatuses ? DATAMODE : NULLMODE);
            }
        } catch (err) {
            setErrorMssg(getErrorMessage(err));
            setStatus(ERROR);
        } finally {
            setSearchIsLoading(false);
        }
    };

    useEffect(() => {
        getStatuses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (value) => {
        setSearch(value);
        setClearSearch(false);
        getStatuses(value);
    };

    const toggleCategory = (globalCategory) => {
        setExpanded((prev) => ({
            ...prev,
            [globalCategory]: !prev[globalCategory],
        }));
    };

    const handleToggleActive = async (item) => {
        try {
            const res = await API.put(
                apiRoute.updateCustomTicketStatus(item.customTicketStatusId),
                { isActive: !item.isActive }
            );
            if (res.status === 200) {
                toastMessage(res?.data?.message || "Status updated");
                getStatuses();
            }
        } catch (err) {
            toastMessage(getErrorMessage(err), ERROR);
        }
    };

    const handleDelete = async (item) => {
        const confirmed = window.confirm(
            `Delete custom status "${item.name}"? This cannot be undone.`
        );
        if (!confirmed) return;

        try {
            const res = await API.delete(
                apiRoute.deleteCustomTicketStatus(item.customTicketStatusId)
            );
            if (res.status === 200) {
                toastMessage(res?.data?.message || "Status deleted");
                getStatuses();
            }
        } catch (err) {
            toastMessage(getErrorMessage(err), ERROR);
        }
    };

    const summaryCards = useMemo(
        () => [
            { label: "TOTAL", value: payload?.summary?.total || 0 },
            { label: "ACTIVE", value: payload?.summary?.active || 0 },
            {
                label: "IN AUTOMATION",
                value: payload?.summary?.inAutomation || 0,
            },
        ],
        [payload]
    );

    return (
        <div className='padded__layout pt-4 custom-ticket-statuses'>
            <div className='custom-ticket-statuses__header'>
                <Heading
                    headingText='Custom Ticket Status Management'
                    headingSubText="Create custom statuses that map to Metacare's global workflow stages."
                />
                <ComponentAccessControl
                    baseResources={CUSTOM_TICKET_STATUS_CREATE_PERMISSION}>
                    <AddButton
                        btnText='Create Ticket Status'
                        onClick={() => setShowCreateModal(true)}
                    />
                </ComponentAccessControl>
            </div>

            <div className='custom-ticket-statuses__summary'>
                {summaryCards.map((card) => (
                    <div
                        className='custom-ticket-statuses__summary-card'
                        key={card.label}>
                        <span>{card.label}</span>
                        <strong>{card.value}</strong>
                    </div>
                ))}
            </div>

            <div className='custom-ticket-statuses__toolbar'>
                <PaginationSearch
                    placeholder='Search statuses'
                    handleSearch={handleSearch}
                    searchIsLoading={searchIsLoading}
                    clearSearch={clearSearch}
                    defaultValue={search}
                />
            </div>

            {status === LOADING && <Loading />}
            {status === ERROR && (
                <ErrorView message={errorMssg} handleRetry={getStatuses} />
            )}
            {status === NULLMODE && (
                <Empty text='No custom ticket statuses yet' />
            )}

            {status === DATAMODE &&
                (payload.categories || []).map((category) => (
                    <section
                        className='custom-ticket-statuses__category'
                        key={category.globalCategory}>
                        <button
                            type='button'
                            className='custom-ticket-statuses__category-header'
                            onClick={() =>
                                toggleCategory(category.globalCategory)
                            }>
                            <div>
                                <div className='custom-ticket-statuses__category-title'>
                                    <span
                                        className={`custom-ticket-statuses__dot custom-ticket-statuses__dot--${category.globalCategory.toLowerCase()}`}
                                    />
                                    <strong>{category.label}</strong>
                                    {category.system && (
                                        <span className='custom-ticket-statuses__system'>
                                            SYSTEM
                                        </span>
                                    )}
                                    <span className='custom-ticket-statuses__count'>
                                        {category.count}{" "}
                                        {category.count === 1
                                            ? "status"
                                            : "statuses"}
                                    </span>
                                </div>
                                <p>{category.description}</p>
                            </div>
                            <span>
                                {expanded[category.globalCategory] ? "▾" : "▸"}
                            </span>
                        </button>

                        {expanded[category.globalCategory] &&
                            (category.statuses || []).map((item) => (
                                <div
                                    className='custom-ticket-statuses__row'
                                    key={item.customTicketStatusId}>
                                    <div>
                                        <div className='custom-ticket-statuses__row-title'>
                                            <strong>{item.name}</strong>
                                            {item.isActive && (
                                                <span className='custom-ticket-statuses__active'>
                                                    Active
                                                </span>
                                            )}
                                        </div>
                                        {item.description && (
                                            <p>{item.description}</p>
                                        )}
                                        <div className='custom-ticket-statuses__meta'>
                                            <span>
                                                Created{" "}
                                                {formatCreatedDate(
                                                    item.createdDate
                                                )}
                                            </span>
                                            <span>
                                                {item.automationCount || 0}{" "}
                                                automations
                                            </span>
                                        </div>
                                    </div>
                                    <div className='custom-ticket-statuses__actions'>
                                        <ComponentAccessControl
                                            baseResources={
                                                CUSTOM_TICKET_STATUS_DELETE_PERMISSION
                                            }>
                                            <ReactSVG
                                                src={asset.icons.trashIcon}
                                                className='custom-ticket-statuses__delete'
                                                onClick={() =>
                                                    handleDelete(item)
                                                }
                                            />
                                        </ComponentAccessControl>
                                        <ComponentAccessControl
                                            baseResources={
                                                CUSTOM_TICKET_STATUS_UPDATE_PERMISSION
                                            }>
                                            <Switch
                                                id={`cts-${item.customTicketStatusId}`}
                                                checked={!!item.isActive}
                                                onChange={() =>
                                                    handleToggleActive(item)
                                                }
                                            />
                                        </ComponentAccessControl>
                                    </div>
                                </div>
                            ))}
                    </section>
                ))}

            <CreateCustomTicketStatusModal
                showModal={showCreateModal}
                closeModal={() => setShowCreateModal(false)}
                handleSuccess={() => {
                    setShowCreateModal(false);
                    setClearSearch(true);
                    getStatuses("");
                }}
            />
        </div>
    );
};

export default CustomTicketStatuses;
